import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { calculateOverallCurrentGPA } from '../../utils/gpa';
import { isCompletedStatus, isInProgressStatus, isPendingStatus, isOverdueStatus } from '../../utils/assignments';
import { getSemesterGroupKey, getMostRelevantAcademicTerm } from '../../utils/semesters';
import Modal from '../Layout/Modal';
import { CourseCompletionTimeline } from './AcademicAnalytics';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => props.compact ? '0.125rem' : '1rem'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const GPACard = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const GPACardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GPAValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const GPALabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;

const GPADetails = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;

const StatDetails = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
`;

const ChartContainer = styled.div`
  margin-top: 2rem;
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
`;

const ChartTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ChartSection = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 1rem;
`;

const ChartSectionTitle = styled.h4`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
`;

const GPAChart = styled.svg`
  width: 100%;
  height: 200px;
  overflow: visible;
`;

const CurrentTermGPAChart = ({ currentGPA, hasGradedAssignments, termName }) => {
  const maxGPA = 4.0;
  const chartWidth = 300;
  const chartHeight = 150;
  const padding = 40;

  const displayGPA = currentGPA;

  return (
    <GPAChart viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
      {/* Grid lines */}
      {[0, 1, 2, 3, 4].map(gpa => (
        <g key={gpa}>
          <line
            x1={padding}
            y1={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding)}
            x2={chartWidth - padding}
            y2={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding)}
            stroke="#e5e7eb"
            strokeWidth="1"
            strokeDasharray="2,2"
          />
          <text
            x={padding - 10}
            y={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#6b7280"
          >
            {gpa}
          </text>
        </g>
      ))}

      {/* GPA point - single circle like Academic Analytics */}
      <circle
        cx={chartWidth / 2}
        cy={chartHeight - padding - (displayGPA / maxGPA) * (chartHeight - 2 * padding)}
        r="8"
        fill="#3b82f6"
        stroke="#ffffff"
        strokeWidth="2"
      >
        <title>{termName}: {displayGPA.toFixed(2)} GPA</title>
      </circle>

      {/* GPA value label above the circle */}
      <text
        x={chartWidth / 2}
        y={chartHeight - padding - (displayGPA / maxGPA) * (chartHeight - 2 * padding) - 15}
        textAnchor="middle"
        fontSize="14"
        fontWeight="600"
        fill="#3b82f6"
      >
        {displayGPA.toFixed(2)}
      </text>

      {/* Term label below */}
      <text
        x={chartWidth / 2}
        y={chartHeight - 10}
        textAnchor="middle"
        fontSize="12"
        fill="#6b7280"
      >
        {termName}
      </text>
    </GPAChart>
  );
};

const TermAnalytics = ({ compact }) => {
  const { assignments, courses, settings } = useData();
  const { user } = useAuth();
  const [accountStats, setAccountStats] = useState({
    averageGrade: 'N/A',
    assignmentsDue: 0
  });
  const [showGPAHelp, setShowGPAHelp] = useState(false);
  const [currentTerm, setCurrentTerm] = useState({ semester: '', year: 0 });
  const [currentTermCourses, setCurrentTermCourses] = useState([]);
  const [hasGradedAssignments, setHasGradedAssignments] = useState(false);

  useEffect(() => {
    if (!courses || !assignments || !user) return;

    const term = getMostRelevantAcademicTerm(courses, assignments, settings);
    const termCourses = (term.courses || []).filter(course =>
      getSemesterGroupKey(course.semester) === term.semesterGroup &&
      String(course.year) === String(term.year)
    );

    setCurrentTerm({ semester: term.displaySemester, year: term.year });
    setCurrentTermCourses(termCourses);

    const currentTermCourseIds = termCourses.map(course => String(course._id || course.id));

    // Filter assignments to only those from current term courses
    const filteredAssignments = assignments?.filter(assignment => {
      const assignmentCourseId = typeof assignment.course === 'object' ? assignment.course?._id : assignment.course;
      const matches = currentTermCourseIds.includes(String(assignmentCourseId));
      return matches;
    }) || [];

    // Calculate credit-weighted GPA using current term data only
    const averageGPA = calculateOverallCurrentGPA(termCourses, filteredAssignments, settings);

    // Check if there are any graded assignments or courses
    const graded = filteredAssignments.some(a => a.grade) || termCourses.some(c => c.grade);
    setHasGradedAssignments(graded);

    // Assignments still due (in progress + pending from current term)
    const inProgress = filteredAssignments.filter(a => isInProgressStatus(a.status)).length;
    const pending = filteredAssignments.filter(a => isPendingStatus(a.status)).length;
    const assignmentsDue = inProgress + pending;

    setAccountStats({
      averageGrade: averageGPA.toFixed(2),
      assignmentsDue
    });
  }, [courses, assignments, user, settings]);

  // Create a map of course IDs to course status for quick lookup
  const courseStatusMap = (courses || []).reduce((map, course) => {
    map[course._id || course.id] = course.status;
    return map;
  }, {});

  // Filter assignments to only active (non-completed) courses
  const activeAssignments = (assignments || []).filter(assignment => {
    const courseId = assignment.course?._id || assignment.course?.id || assignment.course;
    return courseStatusMap[courseId] !== 'completed';
  });

  // Calculate completion rate for active assignments
  const totalAssignments = activeAssignments.length;
  const completedAssignments = activeAssignments.filter(a => isCompletedStatus(a.status)).length;
  const completionRate = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;

  // Calculate overdue assignments for active assignments
  const overdueAssignments = activeAssignments.filter(a => isOverdueStatus(a.status)).length;

  // Calculate average assignments per course for current term
  const activeCourses = (courses || []).filter(c => c.status !== 'completed').length;
  const avgAssignmentsPerCourse = activeCourses > 0 ? Math.round(totalAssignments / activeCourses) : 0;

  return (
    <Container compact={compact}>
      <Grid>
        <GPACard compact={compact}>
          <GPACardContent>
            <div>
              <GPALabel>Term GPA</GPALabel>
              <GPAValue>{accountStats.averageGrade}</GPAValue>
              <GPADetails>{currentTerm.semester} {currentTerm.year} courses</GPADetails>
            </div>
          </GPACardContent>
        </GPACard>

        <StatCard compact={compact}>
          <StatLabel>Assignments Due</StatLabel>
          <StatValue>{accountStats.assignmentsDue}</StatValue>
          <StatDetails>{currentTerm.semester} {currentTerm.year} assignments</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Completion Rate</StatLabel>
          <StatValue>{completionRate}%</StatValue>
          <StatDetails>{completedAssignments} of {totalAssignments} assignments</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Overdue Assignments</StatLabel>
          <StatValue>{overdueAssignments}</StatValue>
          <StatDetails>Need immediate attention</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Active Courses</StatLabel>
          <StatValue>{activeCourses}</StatValue>
          <StatDetails>{currentTerm.semester} {currentTerm.year} enrollment</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Avg Assignments/Course</StatLabel>
          <StatValue>{avgAssignmentsPerCourse}</StatValue>
          <StatDetails>{currentTerm.semester} {currentTerm.year} workload</StatDetails>
        </StatCard>
      </Grid>

      <ChartContainer compact={compact}>
        <ChartTitle>{currentTerm.semester} {currentTerm.year} Progress</ChartTitle>
        <ChartGrid>
          <ChartSection>
            <ChartSectionTitle>{currentTerm.semester} {currentTerm.year} GPA</ChartSectionTitle>
            <CurrentTermGPAChart
              currentGPA={parseFloat(accountStats.averageGrade) || 0}
              hasGradedAssignments={hasGradedAssignments}
              termName={`${currentTerm.semester} ${currentTerm.year}`}
            />
          </ChartSection>
          <ChartSection>
            <ChartSectionTitle>{currentTerm.semester} {currentTerm.year} Courses</ChartSectionTitle>
            <CourseCompletionTimeline courses={currentTermCourses} settings={settings} />
          </ChartSection>
        </ChartGrid>
      </ChartContainer>

      <Modal
        isOpen={showGPAHelp}
        onClose={() => setShowGPAHelp(false)}
        title="Overall GPA Calculation Help"
      >
        <p>Your overall GPA is calculated based on current term courses and assignments only.</p>
        <p>Completed courses from previous terms are not included in this calculation.</p>
      </Modal>
    </Container>
  );
};

export default TermAnalytics;